import React from 'react';
import classNames from 'classnames';
import Immutable from 'immutable';
import { useTranslation } from 'react-i18next';

import { Container } from '@uncinc/uncinc-react-kitchen-sink';

import {
  Paragraph,
  View,
  Taxonomy,
} from '@uncinc/react-drupal-core';

import {
  Share,
  GetStarted,
  WysiwygField,
  CampaignBlock,
  Hero,
} from '../../../components';

import ExpertsSlider from '../../../components/ExpertsSlider';
import NodeMetadata from '../../../components/NodeMetadata';
import MemberWall from '../../../components/MemberWall';
import NewsletterBlock from '../../../components/NewsletterBlock';
import Tags from '../../../components/Tags';
import useLoggedInStatus from '../../../hooks/useLoggedInstatus';

import './style.scss';

const NodeDownload = ({ entity, className }) => {
  const { t } = useTranslation();
  const { isLoggedIn } = useLoggedInStatus();
  const theme = entity.getIn(['field_theme', 0, 'entity']);
  const classes = classNames(className, 'NodeDownload');
  const created = new Date(Date.parse(entity.getIn(['created', 0, 'value'])));
  const title = entity.getIn(['title', 0, 'value'], '');
  const body = entity.getIn(['body', 0, 'processed']);
  const paragraphs = entity.getIn(['field_paragraphs'], Immutable.Map());
  const downloadType = entity.getIn(['field_download_type', 0, 'entity', 'name', 0, 'value'], 'Download');
  const subtitle = entity.getIn(['field_subtitle', 0, 'value']);
  const experts = entity.getIn(['field_experts'], Immutable.Map());
  const targetAudience = entity.getIn(['field_target_audience', 0, 'processed']);
  const subjects = entity.getIn(['field_subject']);
  const workfields = entity.getIn(['field_workfield']);
  const relatedDossierArgs = entity.getIn(['field_subject'])
    .concat(entity.getIn(['field_workfield']))
    .concat(entity.getIn(['field_phase_of_life']))
    .concat(entity.getIn(['field_locations']));
  const expertsTitle = entity.getIn(['field_experts_title', 0, 'value'], t('expert.node_title'));
  const heroImage = entity.getIn(['field_media_image', 0], Immutable.Map());
  const campaign = entity.getIn(['field_campaign', 0, 'entity'], null);
  const getStartedContent = entity.getIn(['field_get_started_content'], Immutable.Map()).concat(entity.getIn(['field_get_started_tax_content']));
  const isMemberOnlyContent = entity.getIn(['field_member_only_content', 0, 'value'], false);

  const showMemberWall = !isLoggedIn && isMemberOnlyContent;

  return (
    <article typeof="schema:WebPage" className={classes}>
      <Hero
        image={heroImage}
      />

      <Container>
        <div className="NodeDownload__content__wrapper row">
          <div className="col-12 col-lg-10 offset-lg-1">
            <div className="NodeDownload__content" id="main">
              <NodeMetadata
                icon="icon-copy"
                displayType={downloadType}
                created={created}
                theme={theme}
              />
              <h1>{title}</h1>

              {subtitle && (
                <div className="NodeArticleDetail__subtitle">
                  {subtitle}
                </div>
              )}

              {body && (
                <WysiwygField
                  property="schema:text"
                  className="NodeDownload__body subheading2"
                  html={body}
                />
              )}

              {targetAudience && (
                <>
                  <h4>Doelgroep</h4>
                  <WysiwygField
                    property="schema:text"
                    className="NodeDownload__body subheading2"
                    html={targetAudience}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </Container>

      <div className="NodeContentWrapper">
        {paragraphs.size > 0 && (
          <div className="Paragraphs">
            {
              paragraphs.map((item, index) => {
                if (showMemberWall && index > 0) return;
                const paragraphId = item.get('target_id');
                const paragraph = item.getIn(['entity']);

                return (
                  <Paragraph key={paragraphId} entity={paragraph} id={paragraphId} />
                );
              })
            }
          </div>
        )}

        <ExpertsSlider
          expertsTitle={expertsTitle}
          experts={experts}
        />

        {campaign && (
          <CampaignBlock entity={campaign} />
        )}

        <GetStarted
          title={entity.getIn(['field_get_started_title', 0, 'value'])}
          subtitle={entity.getIn(['field_get_started_subtitle', 0, 'value'])}
          content={getStartedContent}
        />

        {relatedDossierArgs.size > 0 && (
          <div className="TaxonomySubject__related_dossiers">
            <Container>
              <h2>{entity.getIn(['field_related_dossiers_title', 0, 'value'], 'Dit artikel is onderdeel van')}</h2>
              <View id="article_dossiers_related" display="block_1" args={relatedDossierArgs} />
            </Container>
          </div>
        )}

        {entity.getIn(['field_paragraph_feedback', 0]) && (
          <div className="NodeArticleDetail__feedback">
            {
              entity.getIn(['field_paragraph_feedback']).map((item, index) => {
                const paragraphId = item.get('target_id');
                const paragraph = item.getIn(['entity']);

                return (
                  <Paragraph key={paragraphId} entity={paragraph} id={paragraphId} />
                );
              })
            }
          </div>
        )}

        <Tags subjects={subjects} workfields={workfields} />
        {theme && (
          <Container>
            <div className="row">
              <div className="col-12">
                <Taxonomy
                  id={theme.getIn(['tid', 0, 'value'])}
                  entity={theme}
                  viewMode="card"
                />
              </div>
            </div>
          </Container>
        )}
        <Container>
          <div className="row">
            <div className="col-12 col-lg-8 offset-lg-2">
              <Share pageTitle={entity.getIn(['title', 0, 'value'], '')} />
            </div>
          </div>
        </Container>
      </div>

      {/* Member wall. */}
      {showMemberWall && (
        <MemberWall />
      )}

      {/* Newsletter subscription block */}
      <NewsletterBlock />
    </article>
  );
};

export default NodeDownload;
